import React, { useEffect, useRef, useState } from 'react';
import { BlogCarousel } from '@components/BlogCarousel';
import { FaqList } from '@components/FaqList';
import { Layout, Seo } from '@layout';
import { AbcSection } from '@sections/shared/AbcSection';
import { CenteredFaqSection } from '@sections/shared/CenteredFaqSection';
import { MatchingStationerySection } from '@sections/shared/MatchingStationerySection';
import { PlanTogetherSection } from '@sections/shared/PlanTogetherSection';
import { graphql, useStaticQuery } from 'gatsby';
import {
  CustomizeThemeSection,
  DesignsSectionV2,
  RsvpMadeEasySection,
  TravelSection,
  UnderWrapsSection,
  WWHeroSection,
  SiteToSeeNewSection,
} from '@sections/wedding-website';
import { GuestMobileAppCrossSell } from '@components/GuestMobileAppCrossSell';
import { useThemeMediaQuery } from '@hooks/useThemeMediaQuery';
import { LinkVariant } from '@components/themes';
import TestimonialSection from '@sections/shared/TestimonialSection/TestimonialSection';
import { ConnectedGuestListStatic } from '@sections/wedding-website/ConnectedGuestListSection/ConnectedGuestListStatic';
import { useTranslation } from 'react-i18next';
import { SEOContentSection } from '@sections/shared/SEOContentSection';

const page = 'wedding-website';

const WeddingWebsite = () => {
  const data = useStaticQuery(graphql`
    query {
      weddingWebsiteBlogs {
        id
        blogs {
          link
          title
          excerpt
          mediaLink
        }
      }
      bliss {
        themeVariants(variantTypes: [aloha, brannan]) {
          variationType
          imageUrl
          printUrl
          vendor
          tags
          colors
          font
          variantColor
          variantName
          variantThemeId
          theme {
            id
            name
            themeId
            marketingData {
              variationType
              imageUrl
              printUrl
              vendor
              tags
              colors
              font
              variantColor
              variantName
              variantThemeId
              theme {
                name
                themeId
              }
            }
          }
        }
      }
    }
  `);
  const headerRef = useRef<HTMLElement>(null);
  const isMobile = useThemeMediaQuery('below_sm');
  const [headerLinkVariant, setHeaderLinkVariant] = useState<LinkVariant | undefined>(undefined);

  useEffect(() => {
    if (isMobile) {
      setHeaderLinkVariant('primary');
    } else {
      setHeaderLinkVariant('navLink');
    }
  }, [isMobile]);

  const { t } = useTranslation();
  const seoContent = t('weddingWebsitePage:seoContent');

  return (
    <Layout
      headerRef={headerRef}
      isTransparentHeader={true}
      page={page}
      transparentJoyLogoColor={'purple6'}
      transparentHeaderLinkVariant={headerLinkVariant}
      transparentHeaderButtonVariant="defaultPrimeWeddingWebsites"
    >
      <Seo
        title={t('weddingWebsitePage:seo.title')}
        description={t('weddingWebsitePage:seo.content')}
        canonicalURL={'https://withjoy.com/wedding-website/'}
        ogImage={'common/meta_share/wedding-website.jpg'}
      />
      <WWHeroSection page={page} />
      <DesignsSectionV2 themes={data.bliss.themeVariants} sectionData={t('weddingWebsitePage:sections.designsV2')} />
      <CustomizeThemeSection />
      <SiteToSeeNewSection />
      <PlanTogetherSection backgroundColor="humana1Light" />
      <RsvpMadeEasySection titleColor="neutra6" bodyColor="neutra5" backgroundColor="humana1Light" />
      <UnderWrapsSection />
      <TravelSection />
      <GuestMobileAppCrossSell />
      <MatchingStationerySection />
      <ConnectedGuestListStatic page={page} />
      <CenteredFaqSection
        title={t('weddingWebsitePage:sections.coloredBlock.title')}
        titleColor="blue6"
        backgroundColor="blue1"
        sectionId="Wedding_Websites_FAQ"
      >
        <FaqList accordion={true} questionNodes={[...t('faq:questions.weddingWebsite'), ...t('faq:questions.general')]} color="blue6" />
      </CenteredFaqSection>
      <SEOContentSection
        nodes={seoContent.nodes}
        ctaLabel={seoContent.ctaLabel}
        reviews={seoContent.reviews}
        id="Wedding_Websites_SEO_Content"
        backgroundColor="blue1"
      />
      <BlogCarousel blogs={data.weddingWebsiteBlogs.blogs} sectionId="Wedding_Websites_Blog" />
      <TestimonialSection carouselVariant="left" backgroundColor="blue6" quotes={t('testimonials:website')} mobileQuoteIndex={1} />
      <AbcSection abcSectionId="Wedding_Websites_ABC_CTA" page={page} posterOptimization="default" />
    </Layout>
  );
};

export default WeddingWebsite;
